import { slugifyLeagueName } from "@softmax/web-features/leagues";
import { gamePresentationOf } from "@softmax/web-features/coworlds";
import { gameTheaterQueryKeys, gameResourceHref } from "../../game-types";
import {
  type GameAddressResolution,
  type GamePageContext,
} from "../../game-types";

import {
  type ResolvedGamePageInput,
  canonicalGamePath,
} from "./canonical-path";

export async function resolvedGamePage({
  path,
  resolution,
  selectedUpload,
  requestedLeague,
  defaultFallback,
  viewer,
  frontDoor,
}: ResolvedGamePageInput): Promise<GameAddressResolution> {
  const { url, tail } = path;
  const address = resolution.game_address;

  if (selectedUpload && selectedUpload.game_address?.id !== address.id) {
    return { kind: "not-found" };
  }

  const canonicalPath = await canonicalGamePath({
    path,
    resolution,
    requestedLeague,
    defaultFallback,
    frontDoor,
  });

  if (url.pathname !== canonicalPath) {
    return {
      kind: "navigate",
      navigation: { kind: "path", pathname: canonicalPath, status: 307 },
      viewer,
    };
  }

  const coworldId = selectedUpload?.id ?? resolution.coworld_id;
  const coworldName = selectedUpload?.name ?? resolution.coworld_name;
  const corpusSlug = coworldName
    ? slugifyLeagueName(coworldName)
    : address.slug;
  let presentation: GamePageContext["presentation"] = "theater";

  if (
    tail.length === 0 &&
    !gameTheaterQueryKeys.some((key) => url.searchParams.has(key))
  ) {
    switch (gamePresentationOf(corpusSlug).home) {
      case "store":
        presentation = "home";
        break;
      case "desk":
        presentation = "desk";
        break;
    }
  }

  const common = {
    viewer,
    address,
    corpusSlug,
    publicPath: gameResourceHref(url.href),
    gameId: resolution.game_id,
    hasVisibleLeagues: resolution.has_visible_leagues,
    league: resolution.league
      ? {
          id: resolution.league.id,
          requestedSlug: defaultFallback ? null : requestedLeague,
          public: resolution.league.public !== false,
        }
      : null,
    presentation,
  };

  if (
    (!resolution.has_visible_leagues ||
      selectedUpload?.visibility === "private") &&
    coworldId !== null &&
    coworldName !== null
  ) {
    if (tail.length > 0) return { kind: "not-found" };

    return {
      kind: "resolved",
      context: { ...common, mode: "upload", coworldId, coworldName },
    };
  }

  return {
    kind: "resolved",
    context: { ...common, mode: "league", coworldId, coworldName },
  };
}
